import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { Mascot, type MascotPose } from '@/components/ui/Mascot'
import { HeaderWash } from '@/components/ui/HeaderWash'
import { track } from '@/lib/analytics'

/**
 * Web access gate (mobile-only billing MVP). Liverly has no web checkout —
 * Pro is purchased in the mobile app (RevenueCat), and this screen tells
 * non-Pro users exactly that, with a refresh path for a just-made purchase.
 *
 * Visuals follow the mobile welcome screen (app/(auth)/welcome.tsx): header
 * wash, mascot, centered 32px title + 17px subtitle, the 18px primary button
 * (radius 16, lifted) and the "Already have an account? Sign In" line style.
 */
export function Gate() {
  const { t } = useTranslation()
  const { user, access, recheckAccess, signOut, profileMissing } = useAuth()
  const navigate = useNavigate()
  const [refreshing, setRefreshing] = useState(false)

  // Funnel analytics — same scheme as mobile (paywall_viewed keyed on `source`),
  // so GA4 conversion reports cover web with source=web_gate.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current) return
    if (profileMissing) {
      viewedRef.current = true
      track('web_no_account_shown', { provider: 'social_or_email' })
    } else if (access === 'denied') {
      viewedRef.current = true
      track('paywall_viewed', { source: 'web_gate' })
    }
  }, [profileMissing, access])

  // Leave the gate as soon as access is granted (mobile purchase + refresh).
  useEffect(() => {
    if (access === 'granted') navigate('/diary', { replace: true })
  }, [access, navigate])

  async function onRefresh() {
    setRefreshing(true)
    await recheckAccess()
    setRefreshing(false)
  }

  // Never render the paywall while the access decision is still resolving —
  // a premium user on a cold load must not see upgrade copy for even a frame.
  if (access === 'checking') {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary" />
      </div>
    )
  }

  const signedInLine = (
    // signInText: 16 / regular / opacity .6; signInLink: semiBold, full opacity
    <p className="flex flex-wrap items-center justify-center gap-x-xs py-smd text-center text-[16px] leading-[normal] text-text">
      <span className="max-w-full truncate opacity-60" title={user?.email ?? ''}>
        {t('webapp.paywall.signedInAs', 'Signed in as')} {user?.email}
      </span>
      <span className="opacity-60" aria-hidden>
        ·
      </span>
      <button type="button" onClick={() => void signOut()} className="font-semibold hover:underline">
        {t('settings.signOut', 'Sign Out')}
      </button>
    </p>
  )

  // No Liverly account behind this sign-in: block entirely — account creation
  // + onboarding happen in the mobile app.
  if (profileMissing) {
    return (
      <GateLayout pose="shrugging" title="Liverly" subtitle={t('webapp.authNoSignup', 'New to Liverly? Create your account in the mobile app, then sign in here.')}>
        {signedInLine}
      </GateLayout>
    )
  }

  return (
    <GateLayout
      pose="pointingright"
      title={t('webapp.paywall.title', 'Liverly on the web is a Pro feature')}
      subtitle={t(
        'webapp.paywall.subtitleMobile',
        'Subscribe to Liverly Pro in the mobile app, then use your diary, scans, recipes, and reports from any computer — always in sync with your phone.',
      )}
      hint={t('webapp.paywall.mobileHint', 'Open Liverly on your phone → Settings → Upgrade to Pro.')}
    >
      <button
        type="button"
        onClick={() => void onRefresh()}
        disabled={refreshing}
        className="mb-md w-full rounded-xl bg-primary py-[18px] text-center text-[18px] font-semibold leading-[normal] tracking-[0.5px] text-button-primary-text transition-opacity active:opacity-80 disabled:opacity-60"
        style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.15)' }}
      >
        {refreshing
          ? t('webapp.paywall.checking', 'Checking…')
          : t('webapp.paywall.alreadySubscribed', 'Already subscribed? Refresh')}
      </button>
      {signedInLine}
    </GateLayout>
  )
}

/** Mobile welcome-screen frame: wash, content column, bottom actions (px 24, pb 40). */
function GateLayout({
  pose,
  title,
  subtitle,
  hint,
  children,
}: {
  pose: MascotPose
  title: string
  subtitle: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="relative flex min-h-full w-full flex-col bg-bg">
      <HeaderWash bleed={false} />
      <div className="relative mx-auto flex w-full max-w-content flex-1 flex-col">
        <div className="flex flex-1 flex-col items-center justify-center px-lg pt-[60px]">
          <Mascot pose={pose} size={160} animation="float" />
          <h1 className="mt-lg text-center text-[32px] font-bold leading-[40px] text-text">{title}</h1>
          <p className="mt-smd px-mlg text-center text-[17px] leading-[24px] text-text opacity-60">{subtitle}</p>
          {hint ? <p className="mt-md px-mlg text-center text-[15px] leading-[20px] text-text-secondary">{hint}</p> : null}
        </div>
        <div className="px-lg pb-[40px] pt-lg">{children}</div>
      </div>
    </div>
  )
}
